import React, { useState, useEffect, useRef } from 'react';
import { FaPlay, FaPause, FaStop, FaCog, FaClock, FaFire, FaCoffee, FaBrain } from 'react-icons/fa';

const PomodoroTimer = () => {
  const [timeLeft, setTimeLeft] = useState(25 * 60); // 25 minutes in seconds
  const [isRunning, setIsRunning] = useState(false);
  const [isBreak, setIsBreak] = useState(false);
  const [settings, setSettings] = useState({
    workDuration: 25,
    shortBreak: 5,
    longBreak: 15,
    sessionsBeforeLongBreak: 4
  });
  const [showSettings, setShowSettings] = useState(false);
  const [sessionCount, setSessionCount] = useState(0);
  const [totalFocusTime, setTotalFocusTime] = useState(0);
  const [completedPomodoros, setCompletedPomodoros] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (isRunning) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleTimerComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [isRunning]);

  const handleTimerComplete = () => {
    try {
      const audio = new Audio('/notification.mp3');
      audio.play();
    } catch (err) {
      console.log('Could not play notification sound');
    }
    
    clearInterval(timerRef.current);
    setIsRunning(false);

    if (!isBreak) {
      // Track completed pomodoro
      setCompletedPomodoros(prev => prev + 1);
      setSessionCount(prev => prev + 1);
      setTotalFocusTime(prev => prev + settings.workDuration);
      
      // Determine break type
      if (sessionCount + 1 >= settings.sessionsBeforeLongBreak) {
        setTimeLeft(settings.longBreak * 60);
        setSessionCount(0);
      } else {
        setTimeLeft(settings.shortBreak * 60);
      }
    } else {
      setTimeLeft(settings.workDuration * 60);
    }
    
    setIsBreak(!isBreak);
    
    // Show browser notification
    if (Notification.permission === 'granted') {
      new Notification(isBreak ? 'Break Complete!' : 'Time to take a break!', {
        body: isBreak ? 'Ready to focus?' : 'Good work! Time to recharge.',
        icon: '/favicon.ico'
      });
    }
  };

  const toggleTimer = () => {
    setIsRunning(!isRunning);
  };

  const resetTimer = () => {
    clearInterval(timerRef.current);
    setIsRunning(false);
    setIsBreak(false);
    setTimeLeft(settings.workDuration * 60);
  };

  const updateSettings = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: parseInt(value)
    }));
  };

  const saveSettings = () => {
    // Apply the new settings to the current timer
    if (!isRunning) {
      setTimeLeft(isBreak 
        ? (sessionCount >= settings.sessionsBeforeLongBreak - 1 
            ? settings.longBreak * 60 
            : settings.shortBreak * 60)
        : settings.workDuration * 60
      );
    }
    setShowSettings(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const calculateTotalFocusTimeFormatted = () => {
    const hours = Math.floor(totalFocusTime / 60);
    const minutes = totalFocusTime % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const requestNotificationPermission = async () => {
    if (Notification.permission !== 'granted') {
      await Notification.requestPermission();
    }
  };

  useEffect(() => {
    requestNotificationPermission();
  }, []);

  return (
    <div className="pomodoro-container">
      <div className={`break-indicator ${isBreak ? 'break' : ''}`}>
        {isBreak ? <><FaCoffee /> Break Time</> : <><FaBrain /> Focus Time</>}
      </div>
      
      <div className="timer-display">
        {formatTime(timeLeft)}
      </div>
      
      <div className="timer-controls">
        <button 
          className={`timer-button ${isRunning ? 'pause-button' : 'start-button'}`}
          onClick={toggleTimer}
        >
          {isRunning ? <FaPause /> : <FaPlay />}
          {isRunning ? ' Pause' : ' Start'}
        </button>
        
        <button 
          className="timer-button reset-button"
          onClick={resetTimer}
        >
          <FaStop /> Reset
        </button>
        
        <button 
          className="timer-button settings-button"
          onClick={() => setShowSettings(!showSettings)}
        >
          <FaCog /> Settings
        </button>
      </div>
      
      {showSettings && (
        <div className="timer-settings">
          <h3>Timer Settings</h3>
          <div className="settings-grid">
            <div className="setting-item">
              <label htmlFor="workDuration">Work Duration (minutes)</label>
              <input
                type="number"
                id="workDuration"
                name="workDuration"
                value={settings.workDuration}
                onChange={updateSettings}
                min="1"
                max="60"
              />
            </div>
            
            <div className="setting-item">
              <label htmlFor="shortBreak">Short Break (minutes)</label>
              <input
                type="number"
                id="shortBreak"
                name="shortBreak"
                value={settings.shortBreak}
                onChange={updateSettings}
                min="1"
                max="30"
              />
            </div>
            
            <div className="setting-item">
              <label htmlFor="longBreak">Long Break (minutes)</label>
              <input
                type="number"
                id="longBreak"
                name="longBreak"
                value={settings.longBreak}
                onChange={updateSettings}
                min="5"
                max="60"
              />
            </div>
            
            <div className="setting-item">
              <label htmlFor="sessionsBeforeLongBreak">Sessions before Long Break</label>
              <input
                type="number"
                id="sessionsBeforeLongBreak"
                name="sessionsBeforeLongBreak"
                value={settings.sessionsBeforeLongBreak}
                onChange={updateSettings}
                min="1"
                max="10"
              />
            </div>
          </div>
          
          <div style={{ marginTop: '20px', textAlign: 'right' }}>
            <button 
              className="timer-button start-button" 
              onClick={saveSettings}
              style={{ padding: '8px 16px', minWidth: 'auto' }}
            >
              Save Settings
            </button>
          </div>
        </div>
      )}
      
      <div className="session-stats">
        <div className="stat-item">
          <h4>Sessions Completed</h4>
          <p>{sessionCount}/{settings.sessionsBeforeLongBreak}</p>
        </div>
        
        <div className="stat-item">
          <h4>Total Focus Time</h4>
          <p>{calculateTotalFocusTimeFormatted()}</p>
        </div>
        
        <div className="stat-item">
          <h4>Pomodoros Today</h4>
          <p>{completedPomodoros}</p>
        </div>
      </div>
    </div>
  );
};

export default PomodoroTimer; 